import { DecisionTableName, DecisionTableConfig, CreateDecisionTableConfigRequest, UpdateDecisionTableConfigRequest } from "src/model/DecisionTableConfig";
import { Auth } from 'aws-amplify';
import { pipelineStage, PipelineStageEnum } from "src/config/Config";


const LLM_WORKFLOW_API_GATEWAY_IDS: Record<PipelineStageEnum, string> = {
    [PipelineStageEnum.Development]: 'n3cujx8hx0',
    [PipelineStageEnum.BetaNA]: 'n3cujx8hx0',
    [PipelineStageEnum.BetaEU]: 'n3cujx8hx0',
    [PipelineStageEnum.BetaFE]: 'n3cujx8hx0',
    [PipelineStageEnum.GammaNA]: 'n3cujx8hx0',
    [PipelineStageEnum.GammaEU]: 'n3cujx8hx0',
    [PipelineStageEnum.GammaFE]: 'n3cujx8hx0',
    [PipelineStageEnum.ProdNA]: 'u7pyl0f2ak',
    [PipelineStageEnum.ProdEU]: 'u7pyl0f2ak',
    [PipelineStageEnum.ProdFE]: 'u7pyl0f2ak',
};

export class DecisionTableConfigApiUtil {
    private static get API_BASE_URL(): string {
        const apiGatewayId = LLM_WORKFLOW_API_GATEWAY_IDS[pipelineStage as PipelineStageEnum];
        return `https://${apiGatewayId}.execute-api.us-west-2.amazonaws.com/v1`;
    }
    
    /**
     * Get authentication headers including JWT token for API requests
     * @returns Headers object with Content-Type and Authorization (if authenticated)
     */
    private static async getAuthHeaders(): Promise<HeadersInit> {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };

        // Skip authentication in development mode
        if (pipelineStage === PipelineStageEnum.Development) {
            return headers;
        }

        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            
            return {
                ...headers,
                'Authorization': `Bearer ${token}`,
            };
        } catch (error) {
            console.error('Failed to get authentication token:', error);
            throw new Error('Authentication required. Please log in again.');
        }
    }
    
    static async getDecisionTableNames(): Promise<DecisionTableName[]> {
        try {
            const headers = await DecisionTableConfigApiUtil.getAuthHeaders();
            
            const response = await fetch(`${DecisionTableConfigApiUtil.API_BASE_URL}/decision-table-configs/keys`, {
                method: 'GET',
                headers,
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    throw new Error('Authentication failed. Please log in again.');
                }
                throw new Error(`Failed to fetch decision table names: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            
            // Extract the keys array from the response
            if (data && Array.isArray(data.keys)) {
                return data.keys;
            } else {
                throw new Error('Invalid response format: expected object with keys array');
            }
        } catch (error) {
            console.error('Error fetching decision table names from API:', error);
            throw error;
        }
    }

    static async getDecisionTableConfig(decisionTableName: string): Promise<DecisionTableConfig | null> {
        try {
            const headers = await DecisionTableConfigApiUtil.getAuthHeaders();
            
            const response = await fetch(`${DecisionTableConfigApiUtil.API_BASE_URL}/decision-table-configs/item/${encodeURIComponent(decisionTableName)}`, {
                method: 'GET',
                headers,
            });

            if (response.status === 404) {
                // Configuration not found, return null
                return null;
            }

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    throw new Error('Authentication failed. Please log in again.');
                }
                throw new Error(`Failed to fetch fee configuration: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            
            // Validate that the response has the expected structure
            if (data && typeof data === 'object') {
                return data as DecisionTableConfig;
            } else {
                throw new Error('Invalid response format: expected DecisionTableConfig object');
            }
        } catch (error) {
            console.error('Error fetching fee configuration from API:', error);
            throw error;
        }
    }

    static async createDecisionTableConfig(request: CreateDecisionTableConfigRequest): Promise<DecisionTableConfig> {
        try {
            const headers = await DecisionTableConfigApiUtil.getAuthHeaders();
            
            const response = await fetch(`${DecisionTableConfigApiUtil.API_BASE_URL}/decision-table-configs/item/${encodeURIComponent(request.decisionTableName)}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(request.config),
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    throw new Error('Authentication failed. Please log in again.');
                }
                throw new Error(`Failed to create fee configuration: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            
            // Validate that the response has the expected structure
            if (data && typeof data === 'object') {
                return data as DecisionTableConfig;
            } else {
                throw new Error('Invalid response format: expected DecisionTableConfig object');
            }
        } catch (error) {
            console.error('Error creating fee configuration via API:', error);
            throw error;
        }
    }

    static async updateDecisionTableConfig(request: UpdateDecisionTableConfigRequest): Promise<DecisionTableConfig> {
        try {
            const headers = await DecisionTableConfigApiUtil.getAuthHeaders();
            
            const response = await fetch(`${DecisionTableConfigApiUtil.API_BASE_URL}/decision-table-configs/item/${encodeURIComponent(request.decisionTableName)}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(request.config),
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    throw new Error('Authentication failed. Please log in again.');
                }
                throw new Error(`Failed to update fee configuration: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            
            // Validate that the response has the expected structure
            if (data && typeof data === 'object') {
                return data as DecisionTableConfig;
            } else {
                throw new Error('Invalid response format: expected DecisionTableConfig object');
            }
        } catch (error) {
            console.error('Error updating fee configuration via API:', error);
            throw error;
        }
    }

    static async deleteDecisionTableConfig(decisionTableName: string): Promise<void> {
        try {
            const headers = await DecisionTableConfigApiUtil.getAuthHeaders();
            
            const response = await fetch(`${DecisionTableConfigApiUtil.API_BASE_URL}/decision-table-configs/item/${encodeURIComponent(decisionTableName)}`, {
                method: 'DELETE',
                headers,
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    throw new Error('Authentication failed. Please log in again.');
                }
                throw new Error(`Failed to delete fee configuration: ${response.status} ${response.statusText}`);
            }

            // DELETE operations typically don't return content, so we don't need to parse JSON
        } catch (error) {
            console.error('Error deleting fee configuration via API:', error);
            throw error;
        }
    }
}

export default DecisionTableConfigApiUtil;
